import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, map, Observable, Subscription, tap, throwError, timer } from 'rxjs';

import { AuthCustomerInfo, BookingService } from '../booking-api/client';

import { AnalyticsService } from './analytics.service';

export type LoginError = 'LOGIN_INVALID' | 'UNKNOWN_ERROR';

const TOKEN_KEY = 'usr:atn';
const REFRESH_TOKEN_KEY = 'usr:rtn';
const EXPIRES_AT_KEY = 'usr:exa';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private bookingService = inject(BookingService);
  private analytics = inject(AnalyticsService);
  private router = inject(Router);

  readonly currentUser = signal<AuthCustomerInfo | null>(null);
  readonly isLoggedIn = signal<boolean>(this.hasToken());

  private refreshTimerSubscription: Subscription | null = null;
  private refreshInFlight: Promise<boolean> | null = null;

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  private hasToken(): boolean {
    return !!this.getToken();
  }

  private getExpiresAt(): number | null {
    const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
    return expiresAt === null ? null : Number(expiresAt);
  }

  private setApiToken(token: string): void {
    this.bookingService.configuration.credentials['CustomerTokenAuthorization'] = `Bearer ${token}`;
  }

  private storeTokens(token: string, refreshToken: string, expiresIn: number): void {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + expiresIn * 1000));
    this.setApiToken(token);
  }

  private clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
    delete this.bookingService.configuration.credentials['CustomerTokenAuthorization'];
  }

  private isTokenExpired(): boolean {
    const expiresAt = this.getExpiresAt();
    return expiresAt === null || Date.now() >= expiresAt;
  }

  logoutSilently(): void {
    this.clearTokens();
    this.clearRefreshTimer();
    this.isLoggedIn.set(false);
    this.currentUser.set(null);
  }

  async init(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logoutSilently();
      return;
    }

    const token = this.getToken();
    if (token && !this.isTokenExpired()) {
      this.setApiToken(token);
      this.isLoggedIn.set(true);
      this.scheduleTokenRefresh();
      await this.tryLoadCurrentUser();
      return;
    }

    try {
      const response = await firstValueFrom(this.bookingService.authRefresh({ AuthRefreshRequest: { refreshToken } }));
      this.storeTokens(response.token, response.refreshToken, response.expiresIn);
      this.isLoggedIn.set(true);
      this.scheduleTokenRefresh();
      await this.tryLoadCurrentUser();
    } catch {
      this.logoutSilently();
    }
  }

  private async tryLoadCurrentUser(): Promise<void> {
    try {
      await this.loadCurrentUser();
    } catch {
      // non-fatal: user stays logged in without profile data
    }
  }

  scheduleTokenRefresh(): void {
    this.clearRefreshTimer();

    const expiresAt = this.getExpiresAt() ?? Date.now();
    const delay = Math.max(0, expiresAt - Date.now() - 30_000); // refresh 30s early

    this.refreshTimerSubscription = timer(delay).subscribe(() => void this.refreshTokens());
  }

  private clearRefreshTimer(): void {
    this.refreshTimerSubscription?.unsubscribe();
    this.refreshTimerSubscription = null;
  }

  // refreshes the access token, deduplicating concurrent callers
  // (scheduled refresh, interceptor) into a single request
  ensureFreshToken(): Promise<boolean> {
    this.refreshInFlight ??= this.performTokenRefresh().finally(() => (this.refreshInFlight = null));
    return this.refreshInFlight;
  }

  private async performTokenRefresh(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await firstValueFrom(this.bookingService.authRefresh({ AuthRefreshRequest: { refreshToken } }));
      this.storeTokens(response.token, response.refreshToken, response.expiresIn);
      this.scheduleTokenRefresh();
      return true;
    } catch {
      return false;
    }
  }

  private async refreshTokens(): Promise<void> {
    const refreshed = await this.ensureFreshToken();
    if (!refreshed) {
      await this.logout();
    }
  }

  async logout(): Promise<void> {
    this.logoutSilently();
    await this.router.navigate(['/login']);
  }

  login(email: string, password: string): Observable<void> {
    return this.bookingService.authLogin({ AuthLoginRequest: { email, password } }).pipe(
      tap(response => {
        this.storeTokens(response.token, response.refreshToken, response.expiresIn);
        this.isLoggedIn.set(true);
        this.currentUser.set(response.customer ?? null);
        this.scheduleTokenRefresh();
        this.analytics.captureAccountLogin(email);
      }),
      map(() => void 0),
      catchError((e: HttpErrorResponse) => {
        const error = (e.error as { message?: string } | null)?.message;
        this.analytics.captureAccountLoginFail(email, error);
        return throwError(() => error);
      })
    );
  }

  register(email: string, password: string, info: AuthCustomerInfo): Observable<void> {
    return this.bookingService.authRegister({ AuthRegisterRequest: { email, password, info } }).pipe(
      tap(response => {
        this.storeTokens(response.token, response.refreshToken, response.expiresIn);
        this.isLoggedIn.set(true);
        this.currentUser.set(response.customer ?? null);
        this.scheduleTokenRefresh();
        this.analytics.captureAccountCreate({ email });
      }),
      map(() => void 0),
      catchError((e: HttpErrorResponse) => {
        const error = (e.error as { message?: string } | null)?.message;
        this.analytics.captureAccountCreateFailed({ email });
        return throwError(() => error);
      })
    );
  }

  async loadCurrentUser(): Promise<AuthCustomerInfo> {
    const user = await firstValueFrom(this.bookingService.getCurrentUser());
    this.currentUser.set(user);
    this.isLoggedIn.set(true);

    if (this.getExpiresAt() !== null) {
      this.scheduleTokenRefresh();
    }

    return user;
  }
}
